import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { HexColorPicker } from 'react-colorful';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDsChat } from 'redux/db/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { showConfirmModal } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import {
	ChatMode,
	ChatStatus,
	DesignSystemFont,
	DsMode,
	IconPackId,
} from 'dragonmouse-shared/interfaces';
import { ICON_PACKS } from 'dragonmouse-shared/constants';
import * as api from 'dragonmouse-shared/api/forge';
import IconPackSelector from 'components/Widget/Input/IconPackSelector';
import FontSelector from 'components/Widget/Input/FontSelector';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import * as Base from '../styles';
import * as S from './styles';

// Inline mode row with label, default badge, and color picker
const ModeRowItem = ({
	mode: t,
	onLabelChange,
	onBgChange,
}: {
	mode: DsMode;
	onLabelChange: (modeId: string, newLabel: string) => void;
	onBgChange: (modeId: string, newBg: string) => void;
}) => {
	const [isColorOpen, setIsColorOpen] = useState(false);
	const [localLabel, setLocalLabel] = useState(t.label);
	const [bgDraft, setBgDraft] = useState(t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT);
	const popoverRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setLocalLabel(t.label);
	}, [t.label]);

	useEffect(() => {
		setBgDraft(t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT);
	}, [t.bg]);

	// Commit draft color when popover closes
	useEffect(() => {
		if (!isColorOpen && bgDraft !== (t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT)) {
			onBgChange(t.id, bgDraft);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- commit only when popover closes
	}, [isColorOpen]);

	// Close color popover on outside click
	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				setIsColorOpen(false);
			}
		};
		if (isColorOpen) {
			document.addEventListener('mousedown', onMouseDown);
			return () => document.removeEventListener('mousedown', onMouseDown);
		}
	}, [isColorOpen]);

	return (
		<S.ModeRow>
			<S.ModeColorPicker ref={popoverRef}>
				<S.ModeSwatch $color={bgDraft} onClick={() => setIsColorOpen((prev) => !prev)} />
				{isColorOpen && (
					<S.ModeColorPopover>
						<HexColorPicker color={bgDraft} onChange={setBgDraft} />
						<S.ModeColorHexInput
							value={bgDraft}
							onChange={(e) => setBgDraft(e.target.value)}
							onBlur={() => onBgChange(t.id, bgDraft)}
						/>
					</S.ModeColorPopover>
				)}
			</S.ModeColorPicker>
			<S.ModeLabelInput
				value={localLabel}
				onChange={(e) => setLocalLabel(e.target.value)}
				onBlur={(e) => onLabelChange(t.id, e.target.value)}
			/>
			{t.default && <S.ModeDefaultBadge>default</S.ModeDefaultBadge>}
		</S.ModeRow>
	);
};

const FoundationsView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, activeDesignSystem, activeTab, dsChat } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeTab: selectActiveDsTab(r.mode),
			dsChat: selectActiveDsChat(r.db, r.mode.activeDsChatId),
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);
	const { iconPack: reduxIconPack, fonts: reduxFonts, display } = designSystemContent?.config ?? {};
	const modes = display?.modes;

	const [localFonts, setLocalFonts] = useState<DesignSystemFont[] | undefined>(reduxFonts);
	const isReconciling = useRef(false);

	useEffect(() => {
		setLocalFonts(reduxFonts);
	}, [reduxFonts]);

	const updateIconPack = async (newPack: IconPackId) => {
		if (!activeDesignSystem?.id) return;
		await forge.updateDsFiles({
			designSystemId: activeDesignSystem.id,
			files: [{ path: 'config/icons.json', content: JSON.stringify({ pack: newPack }) }],
		});
	};

	const reconcileIconPack = async (oldPack: IconPackId, newPack: IconPackId) => {
		if (!dsChat?.id) {
			toast.error('Cannot reconcile: no active chat');
			return;
		}
		const message = [
			`The icon pack was changed from ${ICON_PACKS[oldPack].name} to ${ICON_PACKS[newPack].name}.`,
			"Please update all component icon references to use the new pack's syntax and icon names.",
		].join(' ');

		await forge.updateDsChat({
			update: {
				id: dsChat.id,
				metadata: { status: ChatStatus.PROCESSING, statusMessage: '' },
			},
		});

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message,
			attachments: [],
		};
		await forge.dsChatAddUserEntry.withToast()({
			dsChatId: dsChat.id,
			entry,
			chatMode: ChatMode.DESIGN_AND_CHAT,
		});
	};

	const onIconPackChange = (newPack: IconPackId) => {
		if (!activeDesignSystem?.id || newPack === reduxIconPack) return;
		const oldPack = reduxIconPack ?? 'phosphor';
		const hasDsChat = !!dsChat?.id;

		dispatch(
			showConfirmModal({
				title: 'Change Icon Pack',
				description: `Switching to ${ICON_PACKS[newPack].name} will require updating icon references in your components.${hasDsChat ? ' Reconcile uses chat to update the entire DS to match the new icon pack' : ''}`,
				submitLabel: hasDsChat ? 'Change & Reconcile' : 'Change',
				altActionLabel: hasDsChat ? 'Change Only' : undefined,
				cancelLabel: 'Cancel',
				onSubmit: hasDsChat
					? async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
								return;
							}
							try {
								await reconcileIconPack(oldPack, newPack);
							} catch (err) {
								console.error('Failed to reconcile icons:', err);
								toast.error('Icon pack updated but failed to reconcile icons');
							}
						}
					: async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
							}
						},
				onAltAction: hasDsChat
					? async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
							}
						}
					: undefined,
			})
		);
	};

	const onFontsCommit = async (_index: number, newFonts: DesignSystemFont[]) => {
		if (!activeDesignSystem?.id) return;
		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystem.id,
				files: [{ path: 'config/fonts.json', content: JSON.stringify(newFonts) }],
			});
		} catch (err) {
			console.error('Failed to update fonts:', err);
		}
	};

	const updateDisplayJson = async (merge: (existing: any) => any) => {
		if (!activeDesignSystem?.id) return;

		const displayFile = designSystemContent?.files?.find((f) => f.path === 'config/display.json');
		let existing: any = {};
		if (displayFile?.content) {
			try {
				existing = JSON.parse(displayFile.content);
			} catch (parseErr) {
				console.warn(
					'Failed to parse existing display.json, starting with empty object:',
					parseErr
				);
			}
		}

		const merged = merge(existing);

		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystem.id,
				files: [{ path: 'config/display.json', content: JSON.stringify(merged) }],
			});
		} catch (err) {
			console.error('Failed to update display config:', err);
			toast.error('Failed to update display config');
		}
	};

	const onModeLabelChange = (modeId: string, newLabel: string) => {
		updateDisplayJson((existing) => ({
			...existing,
			modes: (existing.modes ?? []).map((t: any) =>
				t.id === modeId ? { ...t, label: newLabel } : t
			),
		}));
	};

	const onModeBgChange = (modeId: string, newBg: string) => {
		updateDisplayJson((existing) => ({
			...existing,
			modes: (existing.modes ?? []).map((t: any) => (t.id === modeId ? { ...t, bg: newBg } : t)),
		}));
	};

	const missingFonts = designSystemContent?.missingFonts;
	const isChatProcessing =
		dsChat?.metadata?.status === ChatStatus.PROCESSING ||
		dsChat?.metadata?.status === ChatStatus.STOPPABLE;

	const onReconcileFonts = async () => {
		if (!dsChat?.id || isChatProcessing || !missingFonts?.length || isReconciling.current) return;
		isReconciling.current = true;
		try {
			const message = [
				`The following fonts are used in theme CSS but not configured in fonts.json: ${missingFonts.join(
					', '
				)}.`,
				'Please update typography tokens or font configuration to resolve the mismatch.',
			].join(' ');

			await forge.updateDsChat({
				update: {
					id: dsChat.id,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: '' },
				},
			});

			const entry: api.AddUserEntryDsChatRequest['entry'] = {
				kind: 'user',
				message,
				attachments: [],
			};
			await forge.dsChatAddUserEntry.withToast()({
				dsChatId: dsChat.id,
				entry,
				chatMode: ChatMode.DESIGN_AND_CHAT,
			});
		} finally {
			isReconciling.current = false;
		}
	};

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionContent>
				<S.CardsContainer>
					{modes && modes.length > 0 && (
						<S.FoundationCard>
							<S.CardTitle>Modes</S.CardTitle>
							<S.CardDescription>
								Configure the user-facing names and preview background colors for each mode. To add
								or remove modes, or change the default mode, ask chat.
							</S.CardDescription>
							{modes.map((t) => (
								<ModeRowItem
									key={t.id}
									mode={t}
									onLabelChange={onModeLabelChange}
									onBgChange={onModeBgChange}
								/>
							))}
						</S.FoundationCard>
					)}
					<S.FoundationCard>
						<S.CardTitle>Icon Pack</S.CardTitle>
						<S.CardDescription>Choose the icon library for your design system.</S.CardDescription>
						{reduxIconPack && (
							<IconPackSelector value={reduxIconPack} onChange={onIconPackChange} />
						)}
					</S.FoundationCard>

					<S.FoundationCard>
						<S.CardTitle>Fonts</S.CardTitle>
						<S.CardDescription>
							Configure Google Fonts or provide custom fonts for your design system. Select font
							families, weights, and fallbacks.
						</S.CardDescription>
						{missingFonts && missingFonts.length > 0 && (
							<S.FontWarningBanner>
								<S.FontWarningText>
									The theme uses the following missing {missingFonts.length > 1 ? 'fonts' : 'font'}:
									{` `}
									<S.FontWarningFontList>{missingFonts.join(', ')}</S.FontWarningFontList>
								</S.FontWarningText>
								{dsChat && (
									<S.FontWarningAction onClick={onReconcileFonts} disabled={isChatProcessing}>
										Reconcile
									</S.FontWarningAction>
								)}
							</S.FontWarningBanner>
						)}
						{localFonts && (
							<FontSelector
								value={localFonts}
								onChange={setLocalFonts}
								onFontCommit={onFontsCommit}
							/>
						)}
					</S.FoundationCard>
				</S.CardsContainer>
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default FoundationsView;
